import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/common/Button/Button";
import images from "../../../images";
// import CommonModal from "../../../components/common/Modal/ModalCommon"; // Removed CommonModal
import { useTranslation } from "react-i18next";
import Dialog from "../../../components/common/Dialog"; // Imported Dialog
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import CustomInputProps from "../../../components/CustomInputProps/CustomInputProps";
import {
  useGetCalculateTokenQuery,
  useGetUserInfoQuery,
  useGetWithdrawDetailsQuery,
  usePostWithdrawMutation,
} from "../../../services/apiSlice";
import UnderReview from "../../UnderReview/UnderReview";
import FooterContact from "../../../components/Footer/FooterContact";

const WithDraw = () => {
  const navigate = useNavigate();
  // Changed ref name to dialogRef for clarity, though modalRef would still work
  const dialogRef = useRef();
  const { t } = useTranslation();

  const [bankAccount, setBankAccount] = useState("");
  const [rawBankAccount, setRawBankAccount] = useState("");
  const [tempWithdrawAmount, setTempWithdrawAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawId, setWithdrawId] = useState("");
  const debounceTimerRef = useRef(null);

  const { data: userInfo, isLoading, refetch } = useGetUserInfoQuery();
  const isAccountUnderReview = userInfo?.data?.status;

  const [postWithdraw, { isLoading: isWithdrawing }] =
    usePostWithdrawMutation();
  const { isLoadingWithdrawDetails } = useGetWithdrawDetailsQuery(
    { withdraw_id: withdrawId },
    { skip: !withdrawId }
  );

  const [error, setError] = useState({
    withdrawAmount: "",
    bankAccount: "",
  });

  const { data: calculateReceiveAmount } = useGetCalculateTokenQuery(
    { token_amount: withdrawAmount },
    { skip: !withdrawAmount }
  );

  const availableToken = userInfo?.data?.token || "0";

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (userInfo?.data?.phone) {
      const { phone } = userInfo.data;
      const formattedPhone =
        phone.length > 4 ? `${phone.slice(0, 4)}-${phone.slice(4, 8)}` : phone;
      setBankAccount(phone);
      setRawBankAccount(formattedPhone);

      setError((prev) => ({
        ...prev,
        bankAccount: "",
      }));
    }
  }, [userInfo]);

  const handleBankAccountChange = (value) => {
    if (value === "") {
      setBankAccount("");
      setRawBankAccount("");
      setError((prev) => ({
        ...prev,
        bankAccount: t("withDraw.bankAccountRequired"),
      }));
    } else {
      setError((prev) => ({
        ...prev,
        bankAccount: "",
      }));
      const cleanedValue = value.replace(/[^0-9]/g, "");
      setBankAccount(cleanedValue);
      const formattedPhone =
        cleanedValue.length > 4
          ? `${cleanedValue.slice(0, 4)}-${cleanedValue.slice(4, 8)}`
          : cleanedValue;
      setRawBankAccount(formattedPhone);
    }
  };

  const debouncedSetWithdrawAmount = useCallback((value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setWithdrawAmount(value);
    }, 500);
  }, []);

  const handleWithdrawAmountChange = (value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setTempWithdrawAmount(value);

      if (value === "") {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.withdrawAmountRequired"),
        }));
      } else if (Number(value) > Number(availableToken)) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.tokenExceedsBalance"),
        }));
      } else if (Number(value) <= 0) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.tokenLessThanZero"),
        }));
      } else if (Number(value) < 100) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.theMiniumAmount", { minimum: 100 }),
        }));
      } else if (Number(value) % 100 !== 0) {
        setError((prev) => ({
          ...prev,
          withdrawAmount: t("withDraw.multipleOfHundred"),
        }));
      } else {
        setError((prev) => ({
          ...prev,
          withdrawAmount: "",
        }));
      }
      debouncedSetWithdrawAmount(value);
    }
  };

  const openWithdrawModal = () => {
    if (
      (error.withdrawAmount !== "" && error.withdrawAmount !== undefined) ||
      (error.bankAccount !== "" && error.bankAccount !== undefined)
    ) {
      return;
    }

    if (!bankAccount) {
      setError((prev) => ({
        ...prev,
        bankAccount: t("withDraw.bankAccountRequired"),
      }));
      return;
    } else if (!withdrawAmount) {
      setError((prev) => ({
        ...prev,
        withdrawAmount: t("withDraw.withdrawAmountRequired"),
      }));
      return;
    } else {
      setError({
        withdrawAmount: "",
        bankAccount: "",
      });
      // The Dialog component uses .open() method via useImperativeHandle
      dialogRef.current?.open();
    }
  };

  const handleConfirmWithdraw = async () => {
    try {
      const response = await postWithdraw({
        bank_account: bankAccount,
        token_amount: withdrawAmount,
      });
      setWithdrawId(response?.data?.data?.data.withdraw_id);
      if (response?.data?.status === 201) {
        await refetch();
        navigate("/profile/withdraw/success", {
          state: {
            withdrawId: response?.data?.data?.data.withdraw_id,
            direction: 1,
          },
          replace: false,
        });
      }
    } catch (error) {
      // Handle error
    }
  };

  const handleClose = () => {
    navigate("/profile", {
      state: { direction: -1 },
      replace: false,
    });
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.loading")}
      </div>
    );
  }

  const topContent = (
    <div className="flex flex-col gap-6">
      <div className="w-[42px] h-[42px] rounded-full">
      <div onClick={handleClose} className="w-full h-full flex items-center justify-center">
        <span className="icon icon-arrow-left text-[18px] text-mid-gray" />
      </div>
      </div>
      <h1 className="text-[22px] xs:text-2xl font-bold text-primary">
        {t("withDraw.withdraw")}
      </h1>
    </div>
  );

  return (
    <>
      {isAccountUnderReview === "under-reviewing" ? (
        <UnderReview />
      ) : (
        <>
          <LoadingCommon
            statusLoading={isLoadingWithdrawDetails || isWithdrawing}
          />
          <div className="flex flex-col w-full max-w-[480px] h-full bg-white overflow-y-auto">
            <div className="flex flex-col flex-1 gap-6 px-[24px] pb-[120px]">
              {topContent}
              <div className="pb-6 border-b border-[#E9E6E4]">
                <div className="flex justify-between bg-[#ECEEFF] rounded-md p-4">
                  <div className="flex flex-col gap-4">
                  <span
                      className={`icon icon-rally-coin text-[#253FF6] text-[30px] xs:text-[37px]`}
                    />
                    <p className="text-[#253FF6] text-[18px] xs:text-xl font-bold">
                      {t("quest.taskQuest.rallyCoins")}
                    </p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="text-[#253FF6] text-4xl xs:text-5xl font-bold">
                      {parseInt(availableToken)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-[18px]">
                <p className="text-[#333333] font-bold text-[16px] xs:text-lg">
                  {t("withDraw.enterCoinsToWithdraw")}
                </p>
                <CustomInputProps
                  value={tempWithdrawAmount}
                  onChange={handleWithdrawAmountChange}
                  icon={
                    <span
                      className={`icon icon-rally-coin text-[#253FF6] text-[20px] xs:text-[23px]`}
                    />
                  }
                  isCurrencyInput={true}
                  classNameInput={"text-black !text-[20px] xs:!text-[24px]"}
                  error={error.withdrawAmount}
                  iconDropdown={false}
                  classWrapper={"!pr-0 !border-none"}
                />
                {error.withdrawAmount && (
                  <p className="text-xs text-[#EF4056] mt-1">
                    {error.withdrawAmount}
                  </p>
                )}
                <div>
                  <p className="text-[#A0A0A0] text-sm font-medium">
                    - {t("withDraw.withdrawalFee")}: $
                    {calculateReceiveAmount?.data?.transaction_fee > 0
                      ? Number(
                          calculateReceiveAmount?.data?.transaction_fee
                        ).toFixed(2)
                      : "0.50"}
                  </p>
                </div>
                <div className="flex items-center justify-start gap-1.5">
                  <p className="text-[#333333] font-bold text-[16px] xs:text-lg align-middle">
                    =
                  </p>
                  <img src={images.FlagImage} alt="" className="w-6 h-6" />
                  <span
                    className={`${
                      calculateReceiveAmount?.data?.receive_amount &&
                      error.withdrawAmount === ""
                        ? "text-[#333333]"
                        : "text-[#E9E6E4]"
                    } font-bold text-xl xs:text-2xl`}
                  >
                    $
                    {calculateReceiveAmount?.data?.receive_amount &&
                    error.withdrawAmount === ""
                      ? calculateReceiveAmount?.data?.transaction_fee > 0
                        ? calculateReceiveAmount?.data?.receive_amount.toFixed(
                            2
                          )
                        : (
                            calculateReceiveAmount?.data?.receive_amount - 0.5
                          ).toFixed(2)
                      : "0.00"}
                  </span>
                </div>
                <div className="border-l border-[#E9E6E4] w-5 h-5 pl-2"></div>
                <p className="text-[#333333] font-bold text-[16px] xs:text-lg">
                  {t("withDraw.enterPaynowAccount")}
                </p>
                <CustomInputProps
                  value={rawBankAccount || ""}
                  onChange={handleBankAccountChange}
                  countryCode="+65"
                  iconImage={images.PayNow}
                  placeholder={"0000-0000"}
                  error={error.bankAccount}
                  readOnly={true}
                  icon={
                    <span className="icon-paynow text-[18px] xs:text-xl">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                    </span>
                  }
                />
                {error.bankAccount && (
                  <p className="text-xs text-[#EF4056] mt-1">
                    {error.bankAccount}
                  </p>
                )}
              </div>
              <Button
                text={t("withDraw.withdraw")}
                className="bg-[#253FF6] text-white font-medium text-[18px] xs:text-xl rounded-md h-[50px] py-3 px-[18px]"
                onClick={openWithdrawModal}
                disabled={
                  bankAccount === "" ||
                  withdrawAmount === "" ||
                  error.withdrawAmount !== "" ||
                  error.bankAccount !== "" ||
                  isWithdrawing ||
                  isLoadingWithdrawDetails
                }
              />
              <div className="mt-12">
                <p
                  onClick={() => navigate("/profile/history")}
                  className="text-[#A0A0A0] text-[14px] xs:text-base font-medium text-center underline cursor-pointer"
                >
                  {t("historyWithdraw.withdrawHistory")}
                </p>
              </div>
            </div>

            {/* Replaced CommonModal with Dialog */}
            <Dialog hiddenCloseButton={true} ref={dialogRef}>
              <div className="flex flex-col gap-6 text-center">
                <h2 className="text-[#253FF6] font-medium text-[20px] xs:text-2xl">
                  {t("withDraw.confirmWithdraw")}
                </h2>
                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex flex-col items-center justify-center gap-[6px]">
                    <span
                      className={`icon icon-rally-coin text-[#253FF6] text-[22px] xs:text-[24px]`}
                    />
                    <p className="text-[#253FF6] font-bold text-xl xs:text-2xl">
                      {withdrawAmount}
                    </p>
                  </div>
                  <div className="text-[#E9E6E4] flex items-center justify-center">
                    <span className="text-[13px] xs:text-[15px]">|</span>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-[6px]">
                    <img src={images.FlagImage} alt="" className="w-6 h-6" />
                    <p className="text-[#333333] font-bold text-xl xs:text-2xl">
                      $
                      {calculateReceiveAmount?.data?.receive_amount &&
                      error.withdrawAmount === ""
                        ? calculateReceiveAmount?.data?.transaction_fee > 0
                          ? calculateReceiveAmount?.data?.receive_amount.toFixed(
                              2
                            )
                          : (
                              calculateReceiveAmount?.data?.receive_amount - 0.5
                            ).toFixed(2)
                        : 0.0}
                    </p>
                  </div>
                  <div className="text-[#E9E6E4] flex items-center justify-center">
                    <span className="text-[13px] xs:text-[15px]">|</span>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-3">
                  <span className="icon-paynow text-[22px] xs:text-[24px]">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                    </span>
                    <p className="text-[#333333] font-bold text-xl xs:text-2xl">{`+65 ${rawBankAccount}`}</p>
                  </div>
                </div>

                <div className="flex gap-4 w-full">
                  <Button
                    text={t("base.cancel")}
                    className="flex-1 px-[18px] py-[12px] text-[18px] xs:text-xl font-medium rounded-[6px] min-h-[40px] bg-white border border-[#253FF6] text-[#253FF6]"
                    onClick={() => dialogRef.current.close()}
                  />
                  <Button
                    text={t("base.confirm")}
                    className="flex-1 px-[18px] py-[12px] rounded-[6px] min-h-[40px] text-white text-[18px] xs:text-xl font-medium bg-[#253FF6]"
                    variant="primary"
                    onClick={handleConfirmWithdraw}
                    disabled={
                      bankAccount === "" ||
                      withdrawAmount === "" ||
                      isWithdrawing ||
                      isLoadingWithdrawDetails
                    }
                  />
                </div>
              </div>
            </Dialog>
          </div>
          <FooterContact className={"!bg-white"} />
        </>
      )}
    </>
  );
};

export default WithDraw;
